import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Archive, ArrowLeft, Copy, Globe, Loader2, MoreHorizontal, Save, Send, Trash2, Undo2 } from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { toast } from '@/components/ui/Toaster';
import { cn } from '@/lib/utils';
import { formsApi, type FormDetail, type FormField, type FormStatus } from '@/lib/api';
import { FormBuilder } from '@/components/forms/FormBuilder';
import { AssignPanel } from '@/components/forms/AssignPanel';
import { ResponsesPanel } from '@/components/forms/ResponsesPanel';
import { FORM_STATUS_CLASSES, FORM_STATUS_LABELS } from '@/components/forms/form-utils';

type Tab = 'build' | 'assign' | 'responses';

interface Draft {
  title: string;
  description: string;
  fields: FormField[];
  openSubmissions: boolean;
}

function draftFrom(form: FormDetail): Draft {
  return { title: form.title, description: form.description ?? '', fields: form.fields ?? [], openSubmissions: form.openSubmissions };
}

function sameDraft(a: Draft, b: Draft) {
  return a.title === b.title && a.description === b.description && a.openSubmissions === b.openSubmissions && JSON.stringify(a.fields) === JSON.stringify(b.fields);
}

/** /forms/manage/:formId — Build · Assign · Responses. */
export function FormEditor() {
  const { formId } = useParams<{ formId: string }>();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const tab = (params.get('tab') as Tab) || 'build';
  const openResponseId = params.get('response');
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const { data: form, isLoading, isError, refetch } = useQuery({
    queryKey: ['form', formId],
    queryFn: () => formsApi.get(formId!),
    enabled: !!formId,
  });

  const { data: responsesData, refetch: refetchResponses } = useQuery({
    queryKey: ['formResponses', formId],
    queryFn: () => formsApi.listResponses(formId!),
    enabled: !!formId,
  });
  const responses = responsesData?.responses ?? [];

  const [draft, setDraft] = useState<Draft | null>(null);
  const saved = useMemo(() => (form ? draftFrom(form) : null), [form]);
  const prevSaved = useRef<Draft | null>(null);
  useEffect(() => {
    // Re-seed the editor when the server copy changes, unless there are local edits.
    if (!saved) return;
    setDraft((d) => (!d || !prevSaved.current || sameDraft(d, prevSaved.current) ? saved : d));
    prevSaved.current = saved;
  }, [saved]);

  const dirty = !!draft && !!saved && !sameDraft(draft, saved);

  useEffect(() => {
    if (!dirty) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [dirty]);

  useEffect(() => {
    const onClick = (e: MouseEvent) => { if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false); };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const invalidateLists = () => {
    queryClient.invalidateQueries({ queryKey: ['formsManage'] });
    queryClient.invalidateQueries({ queryKey: ['projectForms'] });
    queryClient.invalidateQueries({ queryKey: ['myForms'] });
  };

  const saveMutation = useMutation({
    mutationFn: (payload: Partial<Draft> & { status?: FormStatus }) => formsApi.update(formId!, payload),
    onSuccess: (updated) => {
      const merged = { ...(queryClient.getQueryData(['form', formId]) as FormDetail), ...updated };
      queryClient.setQueryData(['form', formId], merged);
      // The server normalises (trims labels, dedupes options) — adopt its copy so "dirty" is honest.
      const next = draftFrom(merged);
      prevSaved.current = next;
      setDraft(next);
      invalidateLists();
    },
    onError: (e: any) => toast({ type: 'error', title: "Couldn't save", message: e?.message }),
  });

  const save = async (extra?: { status?: FormStatus }) => {
    if (!draft) return;
    const payload = { title: draft.title.trim(), description: draft.description, fields: draft.fields, openSubmissions: draft.openSubmissions, ...extra };
    if (!payload.title) {
      toast({ type: 'warning', title: 'Give the form a title first' });
      return;
    }
    const blank = draft.fields.find((f) => !f.label.trim());
    if (blank) {
      toast({ type: 'warning', title: 'Every question needs a label', message: `Question ${draft.fields.indexOf(blank) + 1} is blank.` });
      return;
    }
    await saveMutation.mutateAsync(payload);
    if (extra?.status === 'published') toast({ type: 'success', title: 'Published', message: 'You can assign it now.' });
    else if (extra?.status === 'archived') toast({ type: 'success', title: 'Archived', message: 'No new responses can be started.' });
    else if (extra?.status === 'draft') toast({ type: 'success', title: 'Unpublished', message: 'The form is a draft again.' });
    else toast({ type: 'success', title: 'Saved' });
  };

  const duplicateMutation = useMutation({
    mutationFn: () => formsApi.duplicate(formId!),
    onSuccess: (copy) => { invalidateLists(); navigate(`/forms/manage/${copy.id}`); toast({ type: 'success', title: 'Duplicated', message: 'You are now editing the copy.' }); },
    onError: (e: any) => toast({ type: 'error', title: "Couldn't duplicate", message: e?.message }),
  });

  const deleteMutation = useMutation({
    mutationFn: () => formsApi.remove(formId!),
    onSuccess: () => { invalidateLists(); toast({ type: 'success', title: 'Form deleted' }); navigate(form?.projectId ? `/projects/${form.projectId}/forms` : '/forms/manage'); },
    onError: (e: any) => toast({ type: 'error', title: "Couldn't delete", message: e?.message }),
  });

  const setTab = (t: Tab) => {
    const next = new URLSearchParams(params);
    if (t === 'build') next.delete('tab');
    else next.set('tab', t);
    next.delete('response');
    setParams(next, { replace: true });
  };
  const setOpenResponse = (rid: string | null) => {
    const next = new URLSearchParams(params);
    next.set('tab', 'responses');
    if (rid) next.set('response', rid);
    else next.delete('response');
    setParams(next, { replace: true });
  };

  if (isError) {
    return (
      <div className="max-w-3xl mx-auto">
        <Card>
          <p className="text-secondary font-medium">Couldn't load this form</p>
          <div className="mt-4 flex gap-2">
            <Button variant="outline" onClick={() => refetch()}>Retry</Button>
            <Link to="/forms/manage" className="btn-ghost">Back to forms</Link>
          </div>
        </Card>
      </div>
    );
  }
  if (isLoading || !draft || !form) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 text-grey-400 animate-spin" />
      </div>
    );
  }

  const submittedCount = responses.filter((r) => r.status === 'submitted').length;
  const outstanding = responses.length - submittedCount;
  const backHref = form.projectId ? `/projects/${form.projectId}/forms` : '/forms/manage';
  const archivedProject = !!form.project?.archivedAt;

  return (
    <div className="w-full">
      <Link to={backHref} className="inline-flex items-center gap-1 text-sm text-grey-500 hover:text-secondary mb-3">
        <ArrowLeft className="w-4 h-4" /> {form.project?.name ? `${form.project.name} forms` : 'Manage forms'}
      </Link>

      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4 mb-5">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-2">
            <span className={cn('px-2 py-0.5 rounded-full text-xs font-medium', FORM_STATUS_CLASSES[form.status])}>{FORM_STATUS_LABELS[form.status]}</span>
            {form.projectId ? <span className="text-xs text-grey-500">{form.project?.name}</span> : <span className="inline-flex items-center gap-1 text-xs text-grey-500"><Globe className="w-3 h-3" /> Global</span>}
            {form.version > 1 && <span className="text-xs text-grey-400">v{form.version}</span>}
            {dirty && <span className="text-xs text-warning">Unsaved changes</span>}
          </div>
          <label htmlFor="form-title" className="sr-only">Title</label>
          <input
            id="form-title"
            value={draft.title}
            onChange={(e) => setDraft({ ...draft, title: e.target.value })}
            className="w-full text-heading-2 text-secondary bg-transparent border-0 border-b border-transparent hover:border-grey-200 focus:border-accent focus:outline-none px-0 py-0.5 transition-colors"
            placeholder="Form title"
            disabled={archivedProject}
          />
          <label htmlFor="form-description" className="sr-only">Description</label>
          <textarea
            id="form-description"
            value={draft.description}
            onChange={(e) => setDraft({ ...draft, description: e.target.value })}
            rows={draft.description.split('\n').length > 1 ? 3 : 1}
            className="mt-1 w-full text-sm text-grey-600 bg-transparent border-0 border-b border-transparent hover:border-grey-200 focus:border-accent focus:outline-none px-0 py-0.5 resize-none transition-colors"
            placeholder="Add a short introduction people will see at the top of the form (optional)"
            disabled={archivedProject}
          />
        </div>

        <div className="flex items-center gap-2 shrink-0">
          {dirty && (
            <Button variant="ghost" size="sm" onClick={() => setDraft(saved!)}>
              <Undo2 className="w-4 h-4 mr-1.5" /> Discard
            </Button>
          )}
          <Button variant={form.status === 'published' ? 'primary' : 'outline'} onClick={() => save()} isLoading={saveMutation.isPending && !saveMutation.variables?.status} disabled={!dirty || archivedProject}>
            <Save className="w-4 h-4 mr-2" /> Save
          </Button>
          {form.status !== 'published' && (
            <Button onClick={() => save({ status: 'published' })} isLoading={saveMutation.isPending && saveMutation.variables?.status === 'published'} disabled={archivedProject}>
              <Send className="w-4 h-4 mr-2" /> Publish
            </Button>
          )}
          <div className="relative" ref={menuRef}>
            <button type="button" onClick={() => setMenuOpen((o) => !o)} className="btn-ghost btn-icon" aria-haspopup="menu" aria-expanded={menuOpen} aria-label="More actions">
              <MoreHorizontal className="w-5 h-5" />
            </button>
            {menuOpen && (
              <div role="menu" className="absolute right-0 mt-1 w-56 bg-white rounded-lg shadow-lg border border-grey-100 py-1 z-20">
                {form.status === 'published' && (
                  <MenuItem onClick={() => { setMenuOpen(false); save({ status: 'draft' }); }} icon={<Undo2 className="w-4 h-4" />}>
                    Unpublish
                  </MenuItem>
                )}
                <MenuItem onClick={() => { setMenuOpen(false); duplicateMutation.mutate(); }} icon={<Copy className="w-4 h-4" />}>
                  Duplicate
                </MenuItem>
                {form.status !== 'archived' ? (
                  <MenuItem onClick={() => { setMenuOpen(false); if (window.confirm('Archive this form? People with it in progress can still finish; no one new can start.')) save({ status: 'archived' }); }} icon={<Archive className="w-4 h-4" />}>
                    Archive
                  </MenuItem>
                ) : (
                  <MenuItem onClick={() => { setMenuOpen(false); save({ status: 'draft' }); }} icon={<Undo2 className="w-4 h-4" />}>
                    Restore as draft
                  </MenuItem>
                )}
                <MenuItem
                  danger
                  disabled={submittedCount > 0}
                  title={submittedCount > 0 ? 'Forms with submissions can only be archived' : undefined}
                  onClick={() => { setMenuOpen(false); if (window.confirm(`Delete "${form.title}"? This cannot be undone.`)) deleteMutation.mutate(); }}
                  icon={<Trash2 className="w-4 h-4" />}
                >
                  Delete
                </MenuItem>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-grey-200 mb-6 overflow-x-auto">
        <nav className="flex gap-6 -mb-px" role="tablist">
          {(
            [
              ['build', 'Build', null],
              ['assign', 'Assign', outstanding || null],
              ['responses', 'Responses', submittedCount || null],
            ] as Array<[Tab, string, number | null]>
          ).map(([t, label, count]) => (
            <button
              key={t}
              role="tab"
              aria-selected={tab === t}
              onClick={() => setTab(t)}
              className={cn('py-3 text-sm font-medium border-b-2 whitespace-nowrap transition-colors', tab === t ? 'border-accent text-secondary' : 'border-transparent text-grey-500 hover:text-secondary')}
            >
              {label}
              {count ? <span className="ml-1.5 text-xs text-grey-400 tabular-nums">({count})</span> : null}
            </button>
          ))}
        </nav>
      </div>

      {tab === 'build' && (
        <div className="space-y-6">
          {form.status === 'published' && submittedCount > 0 && (
            <p className="text-xs text-grey-500 bg-grey-50 border border-grey-100 rounded-lg px-3 py-2">
              {submittedCount} {submittedCount === 1 ? 'response has' : 'responses have'} already been submitted. Editing questions bumps the form version; existing answers keep the questions they were given.
            </p>
          )}
          {form.projectId && (
            <label className="flex items-start gap-3 cursor-pointer card p-4">
              <input type="checkbox" checked={draft.openSubmissions} onChange={(e) => setDraft({ ...draft, openSubmissions: e.target.checked })} className="mt-0.5 w-4 h-4 rounded text-accent border-grey-300 focus:ring-accent" disabled={archivedProject} />
              <span className="text-sm">
                <span className="font-medium text-secondary">Open to all project members</span>
                <span className="block text-grey-500">Anyone in the project can start a response from the Forms tab, as many times as they like — good for intake and requests. Off means only people you assign can fill it.</span>
              </span>
            </label>
          )}
          <FormBuilder fields={draft.fields} onChange={(fields) => setDraft({ ...draft, fields })} disabled={archivedProject} />
        </div>
      )}

      {tab === 'assign' && <AssignPanel form={form} responses={responses} onChanged={() => { refetchResponses(); invalidateLists(); }} onOpenResponse={(rid) => setOpenResponse(rid)} />}

      {tab === 'responses' && (
        <ResponsesPanel
          form={form}
          fields={form.fields}
          responses={responses}
          onChanged={() => { refetchResponses(); invalidateLists(); }}
          openResponseId={openResponseId}
          onOpenResponse={setOpenResponse}
        />
      )}
    </div>
  );
}

function MenuItem({ children, icon, onClick, danger, disabled, title }: { children: React.ReactNode; icon: React.ReactNode; onClick: () => void; danger?: boolean; disabled?: boolean; title?: string }) {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={cn('w-full flex items-center gap-2 px-3 py-2 text-sm text-left transition-colors disabled:opacity-50 disabled:cursor-not-allowed', danger ? 'text-error hover:bg-error/10' : 'text-secondary hover:bg-grey-50')}
    >
      {icon}
      {children}
    </button>
  );
}
